import { InfoCircleOutlined } from '@ant-design/icons';
import { Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BatchDirection, BatchRowView } from '../types';
import { isActiveStatus } from '../utils/batchRules';
import { flowLabelOf, formatBatchTime } from '../utils/formatUtils';
import { BatchStatusTag, CreationMethodTag, FrozenTag } from './BatchTags';

interface BatchTableProps {
    rows: BatchRowView[];
    direction: BatchDirection;
    selectedIds: number[];
    onSelectChange: (ids: number[]) => void;
    onOpenDetail: (row: BatchRowView) => void;
    onCreateBags: (row: BatchRowView) => void;
    onPrintBags: (row: BatchRowView) => void;
    onEndSorting: (row: BatchRowView) => void;
}

const linkClass = 'cursor-pointer whitespace-nowrap border-0 bg-transparent p-0 text-[13px]';

export const BatchTable: React.FC<BatchTableProps> = ({
    rows, direction, selectedIds, onSelectChange, onOpenDetail, onCreateBags, onPrintBags, onEndSorting,
}) => {
    const { t } = useTranslation();
    const inbound = direction === 'INBOUND';

    const columns: ColumnsType<BatchRowView> = useMemo(() => {
        return [
            {
                key: 'batchNumber',
                title: t('wms.sortingBatch.columns.batchNumber'),
                fixed: 'left',
                width: 290,
                render: (_, row) => (
                    <div className="flex items-center gap-1.5 min-w-0">
                        <span className="cursor-pointer text-[#722ed1] whitespace-nowrap" onClick={() => onOpenDetail(row)}>{row.batchNumber}</span>
                        <FrozenTag frozenAt={row.frozenAt} />
                    </div>
                ),
            },
            {
                key: 'destination',
                title: t(inbound ? 'wms.sortingBatch.columns.sourceToDestination' : 'wms.sortingBatch.columns.destination'),
                width: 120,
                align: 'center',
                render: (_, row) => <span className="whitespace-nowrap">{flowLabelOf(row, inbound)}</span>,
            },
            {
                key: 'status',
                title: t('wms.sortingBatch.columns.status'),
                width: 100,
                align: 'center',
                render: (_, row) => <BatchStatusTag status={row.status} />,
            },
            {
                key: 'creationMethod',
                title: (
                    <Tooltip title={t('wms.sortingBatch.columns.creationMethodTip')}>
                        <span className="cursor-help whitespace-nowrap">
                            {t('wms.sortingBatch.columns.creationMethod')} <InfoCircleOutlined className="text-[11px] text-[#86909c]" />
                        </span>
                    </Tooltip>
                ),
                width: 96,
                align: 'center',
                render: (_, row) => <CreationMethodTag method={row.creationMethod} />,
            },
            {
                key: 'creator',
                title: (
                    <Tooltip title={t('wms.sortingBatch.columns.creatorTip')}>
                        <span className="cursor-help whitespace-nowrap">{t('wms.sortingBatch.columns.creator')}</span>
                    </Tooltip>
                ),
                width: 110,
                align: 'center',
                render: (_, row) => <span className="whitespace-nowrap text-[#4e5969]">{row.creator}</span>,
            },
            {
                key: 'createdAt',
                title: t('wms.sortingBatch.columns.createdAt'),
                width: 140,
                align: 'center',
                render: (_, row) => <span className="whitespace-nowrap text-[#4e5969]">{formatBatchTime(row.createdAt)}</span>,
            },
            {
                key: 'total',
                title: (
                    <div className="flex flex-col items-center leading-tight">
                        <span>{t('wms.sortingBatch.columns.total')}</span>
                        <span className="text-[11px] font-normal text-[#86909c]">{t('wms.sortingBatch.columns.totalUnit')}</span>
                    </div>
                ),
                width: 100,
                align: 'center',
                render: (_, row) => <span className="whitespace-nowrap">{row.createdBags} / {row.parcels}</span>,
            },
            {
                key: 'actions',
                title: t('wms.sortingBatch.columns.actions'),
                fixed: 'right',
                width: inbound ? 100 : 220,
                align: 'center',
                render: (_, row) => {
                    if (inbound) {
                        return (
                            <button type="button" className={`${linkClass} text-[#722ed1]`} onClick={() => onOpenDetail(row)}>
                                {t('wms.sortingBatch.actions.viewDetail')}
                            </button>
                        );
                    }
                    // 已结束分拣 / 终态：按钮置灰但仍可点击，点击后提示原因（与原型一致）
                    const blockedClass = isActiveStatus(row.status) ? 'text-[#722ed1]' : 'text-[#c9cdd4]';
                    return (
                        <div className="flex items-center justify-center gap-2.5">
                            <button type="button" className={`${linkClass} ${blockedClass}`} onClick={() => onCreateBags(row)}>
                                {t('wms.sortingBatch.actions.createBags')}
                            </button>
                            <button type="button" className={`${linkClass} text-[#722ed1]`} onClick={() => onPrintBags(row)}>
                                {t('wms.sortingBatch.actions.printBags')}
                            </button>
                            <button type="button" className={`${linkClass} ${blockedClass}`} onClick={() => onEndSorting(row)}>
                                {t('wms.sortingBatch.actions.endSorting')}
                            </button>
                        </div>
                    );
                },
            },
        ];
    }, [t, inbound, onOpenDetail, onCreateBags, onPrintBags, onEndSorting]);

    return (
        <Table<BatchRowView>
            rowKey="id"
            size="small"
            bordered
            columns={columns}
            dataSource={rows}
            pagination={false}
            scroll={{ x: 'max-content' }}
            rowClassName={(row) => (row.isNew ? 'sorting-batch-row-new' : '')}
            locale={{ emptyText: t('wms.sortingBatch.list.empty') }}
            rowSelection={inbound ? undefined : {
                selectedRowKeys: selectedIds,
                onChange: (keys) => onSelectChange(keys.map((k) => Number(k))),
                // 仅「已取消」的批次不可勾选；分拣结束 / 已完成等仍可勾选（供打印等批量操作）
                getCheckboxProps: (row) => {
                    const disabled = row.status === 'CANCELLED';
                    return {
                        disabled,
                        title: disabled ? String(t('wms.sortingBatch.messages.batchTerminated')) : undefined,
                    };
                },
            }}
        />
    );
};
